import type { ConditionField } from '@/components/ui';

import { useContactFieldSuggestions } from './contactFieldSuggestions';

/**
 * Canonical field set for the contact condition builder — the Mongo dotted paths
 * the backend's ConditionTree.CompileToFilter() filters on (the single engine
 * contacts search, segments, and tag auto-rules all share). Contacts filters and
 * segments both consume this list so they stay in parity.
 *
 * Fields marked `counter: true` are backed by a `contact.{value}` counter table;
 * their value input renders as a searchable dropdown of the table's values (see
 * useConditionValueSuggestions + ConditionBuilder).
 */
export const CONTACT_SEARCH_FIELDS: ConditionField[] = [
  // Targets the denormalized lowercased+ASCII-folded name so search is case- and
  // diacritic-insensitive (the backend folds the query value to match — e.g.
  // "Terezka"/"terezka" and "Bořek"→"borek"). `contains` is first so adding a Name
  // row defaults to a substring search rather than an exact match.
  { value: 'search.nameAscii', label: 'Name', operators: ['contains', 'starts_with', 'equals', 'is_set', 'is_empty'] },
  { value: 'profile.firstName', label: 'First name' },
  { value: 'profile.lastName', label: 'Last name' },
  { value: 'contact.primaryEmail', label: 'Email' },
  { value: 'contact.phones', label: 'Phone (any)', operators: ['contains', 'is_set', 'is_empty'] },
  { value: 'profile.residence', label: 'Residence', counter: true },
  { value: 'profile.city', label: 'City', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.interest', label: 'Interest', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.nationality', label: 'Nationality', counter: true },
  { value: 'profile.gender', label: 'Gender', counter: true },
  // Primary orientation fields (counter-backed). Free-text contains/equals.
  { value: 'profile.occupation', label: 'Occupation', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.position', label: 'Position', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.specialization', label: 'Specialization', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.company', label: 'Company', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.educationInstitution', label: 'Education institution', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.region', label: 'Region', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  { value: 'profile.country', label: 'Country', operators: ['contains', 'equals', 'starts_with', 'is_set', 'is_empty'], counter: true },
  {
    value: 'status.active',
    label: 'Active',
    operators: ['equals', 'not_equals'],
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    value: 'status.doNotContact',
    label: 'Do not contact',
    operators: ['equals', 'not_equals'],
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    value: 'status.vip',
    label: 'VIP',
    operators: ['equals', 'not_equals'],
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    value: 'review.status',
    label: 'Review status',
    operators: ['equals', 'not_equals', 'is_set', 'is_empty'],
    options: [
      { value: 'none', label: 'None' },
      { value: 'requiresReview', label: 'Requires review' },
      { value: 'reviewed', label: 'Reviewed' },
      { value: 'autoInvestigation', label: 'Auto Investigation' },
      { value: 'investigated', label: 'Investigated' },
    ],
  },
  // Rating 0-100; numeric operators let you e.g. filter rating.score < 50 and bulk-delete.
  { value: 'rating.score', label: 'Rating', operators: ['lt', 'gt', 'between', 'equals', 'is_set', 'is_empty'] },
  { value: 'tags', label: 'Tag', operators: ['contains', 'not_equals', 'in', 'not_in', 'is_set', 'is_empty'], counter: true },
  { value: 'sources', label: 'Source', operators: ['contains', 'not_equals', 'in', 'not_in'], counter: true },
  { value: 'services.serviceId', label: 'Product', operators: ['contains', 'in', 'not_in', 'is_set'] },
  { value: 'addresses.country', label: 'Address country', operators: ['equals', 'not_equals', 'contains'] },
  { value: 'profile.dateOfBirth', label: 'Date of birth', operators: ['between', 'before', 'after', 'is_set', 'is_empty'] },
  // Education — Mongo matches dotted paths into the education[] array, so each
  // row matches ANY education entry (parity with the old GET education filters).
  { value: 'education.faculty', label: 'Faculty', operators: ['contains', 'equals', 'is_set', 'is_empty'] },
  { value: 'education.institution', label: 'Institution', operators: ['contains', 'equals', 'is_set', 'is_empty'] },
  { value: 'education.field', label: 'Field of study', operators: ['contains', 'equals', 'is_set', 'is_empty'] },
  { value: 'education.location', label: 'Study location', operators: ['contains', 'equals', 'is_set', 'is_empty'] },
  { value: 'education.yearOfStudy', label: 'Year of study', operators: ['equals', 'gt', 'lt'] },
  {
    value: 'education.status',
    label: 'Education status',
    operators: ['equals', 'not_equals'],
    options: [
      { value: 'enrolled', label: 'Enrolled' },
      { value: 'graduated', label: 'Graduated' },
      { value: 'withdrawn', label: 'Withdrawn' },
    ],
  },
  { value: 'audit.createdAt', label: 'Created', operators: ['before', 'after', 'between'] },
  { value: 'audit.updatedAt', label: 'Updated', operators: ['before', 'after', 'between'] },
];

/**
 * Adapter passed to ConditionBuilder's `useValueSuggestions`. Called once per row
 * with the counter-backed field's path (e.g. `profile.country`) or undefined.
 * useContactFieldSuggestions prepends `contact.`; an empty path leaves useTable
 * disabled, so non-counter fields issue no request.
 */
export function useConditionValueSuggestions(counterPath?: string): string[] {
  const { suggestions } = useContactFieldSuggestions(counterPath ?? '');
  return counterPath ? suggestions : [];
}
